import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Comprehensive English stopwords to isolate meaningful feedback keywords
const STOP_WORDS = new Set([
    'a', 'about', 'above', 'after', 'again', 'against', 'all', 'am', 'an', 'and', 'any', 'are', 'aren\'t', 'as', 'at',
    'be', 'because', 'been', 'before', 'being', 'below', 'between', 'both', 'but', 'by',
    'can', 'can\'t', 'cannot', 'could', 'couldn\'t',
    'did', 'didn\'t', 'do', 'does', 'doesn\'t', 'doing', 'don\'t', 'down', 'during',
    'each', 'even',
    'few', 'for', 'from', 'further',
    'had', 'hadn\'t', 'has', 'hasn\'t', 'have', 'haven\'t', 'having', 'he', 'he\'d', 'he\'ll', 'he\'s', 'her', 'here',
    'here\'s', 'hers', 'herself', 'him', 'himself', 'his', 'how', 'how\'s',
    'i', 'i\'d', 'i\'ll', 'i\'m', 'i\'ve', 'if', 'in', 'into', 'is', 'isn\'t', 'it', 'it\'s', 'its', 'itself',
    'just',
    'let\'s', 'like',
    'me', 'more', 'most', 'mustn\'t', 'my', 'myself',
    'no', 'nor', 'not', 'now',
    'of', 'off', 'on', 'once', 'only', 'or', 'other', 'ought', 'our', 'ours', 'ourselves', 'out', 'over', 'own',
    'same', 'shan\'t', 'she', 'she\'d', 'she\'ll', 'she\'s', 'should', 'shouldn\'t', 'so', 'some', 'such',
    'than', 'that', 'that\'s', 'the', 'their', 'theirs', 'them', 'themselves', 'then', 'there', 'there\'s', 'these',
    'they', 'they\'d', 'they\'ll', 'they\'re', 'they\'ve', 'this', 'those', 'through', 'to', 'too',
    'under', 'until', 'up', 'us',
    'very',
    'was', 'wasn\'t', 'we', 'we\'d', 'we\'ll', 'we\'re', 'we\'ve', 'were', 'weren\'t', 'what', 'what\'s', 'when',
    'when\'s', 'where', 'where\'s', 'which', 'while', 'who', 'who\'s', 'whom', 'why', 'why\'s', 'with', 'won\'t',
    'would', 'wouldn\'t',
    'you', 'you\'d', 'you\'ll', 'you\'re', 'you\'ve', 'your', 'yours', 'yourself', 'yourselves',
    // Common conversational filler words in feedback
    'also', 'always', 'really', 'much', 'many', 'well', 'way', 'thing', 'things', 'get', 'got', 'give', 'given',
    'know', 'make', 'made', 'take', 'took', 'see', 'seen', 'think', 'thought', 'go', 'going', 'went', 'come', 'came',
    'lot', 'lots', 'bit', 'etc', 'one', 'two', 'something', 'someone', 'everyone', 'anyone', 'nothing', 'everything',
    'maybe', 'still', 'already', 'quite', 'sure', 'want', 'wanted', 'need', 'needs', 'needed'
]);

class WordCloud extends Component {
    constructor(props) {
        super(props);
        this.state = {
            viewMode: 'cloud', // 'cloud' | 'tree'
            selectedWord: null,
            animateKey: 0,
        };
    }

    // Process raw free text feedback and compute frequencies & importance
    processWords(feedbacks) {
        if (!feedbacks || feedbacks.length === 0) return { words: [], totalResponses: 0, totalKeywords: 0 };

        const validTexts = feedbacks.filter((t) => t && typeof t === 'string' && t.trim().length > 0);
        const freq = {};
        const wordToQuotes = {};

        validTexts.forEach((text) => {
            // Tokenize & normalize
            const rawTokens = text
                .toLowerCase()
                .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"'’“”]/g, ' ')
                .split(/\s+/);

            const seenInThisQuote = new Set();

            rawTokens.forEach((raw) => {
                let w = raw.trim();
                if (w.length < 3) return;
                if (STOP_WORDS.has(w)) return;

                // Simple stem: remove trailing 's' if word > 4 chars and doesn't end in 'ss'
                if (w.endsWith('s') && w.length > 4 && !w.endsWith('ss')) {
                    w = w.slice(0, -1);
                }

                if (STOP_WORDS.has(w)) return;

                freq[w] = (freq[w] || 0) + 1;

                if (!seenInThisQuote.has(w)) {
                    seenInThisQuote.add(w);
                    if (!wordToQuotes[w]) wordToQuotes[w] = [];
                    wordToQuotes[w].push(text);
                }
            });
        });

        const sorted = Object.entries(freq)
            .map(([word, count]) => ({
                word,
                count,
                quotes: wordToQuotes[word] || [],
            }))
            .sort((a, b) => b.count - a.count);

        // Take top 35 most prominent keywords
        const topWords = sorted.slice(0, 35);
        if (topWords.length === 0) return { words: [], totalResponses: validTexts.length, totalKeywords: 0 };

        const maxCount = topWords[0].count;
        const minCount = topWords[topWords.length - 1].count;

        // Enhance with size, tier, and importance weights
        const processedWords = topWords.map((item, index) => {
            const weight = (item.count - minCount) / Math.max(maxCount - minCount, 1);
            // Non-linear scaling for font size (13px to 34px)
            const fontSize = Math.round(13 + Math.pow(weight, 0.65) * 21);

            let tier = 'tier-neutral';
            if (index === 0 && item.count > 1) {
                tier = 'tier-hero';
            } else if (index < Math.max(3, topWords.length * 0.18)) {
                tier = 'tier-primary';
            } else if (index < Math.max(7, topWords.length * 0.45)) {
                tier = 'tier-secondary';
            } else if (index < Math.max(12, topWords.length * 0.7)) {
                tier = 'tier-accent';
            }

            return {
                ...item,
                fontSize,
                tier,
                percentage: Math.round((item.count / validTexts.length) * 100),
            };
        });

        // For cloud layout, arrange organically so top words cluster near the center
        const cloudOrdered = [...processedWords];
        cloudOrdered.sort((a, b) => {
            // Deterministic pseudo-organic hash arrangement
            const hashA = a.word.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 5;
            const hashB = b.word.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 5;
            return hashA - hashB;
        });

        return {
            words: processedWords,
            cloudWords: cloudOrdered,
            totalResponses: validTexts.length,
            totalKeywords: sorted.length,
            maxCount,
        };
    }

    handleWordClick = (wordObj) => {
        if (this.state.selectedWord && this.state.selectedWord.word === wordObj.word) {
            this.setState({ selectedWord: null });
        } else {
            this.setState({ selectedWord: wordObj });
        }
    };

    handleReanimate = () => {
        this.setState((prev) => ({
            animateKey: prev.animateKey + 1,
            selectedWord: null,
        }));
    };

    highlightText(text, targetWord) {
        if (!targetWord) return text;
        const regex = new RegExp(`(${targetWord}[a-z]*)`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, i) =>
            regex.test(part) ? <mark key={i}>{part}</mark> : part
        );
    }

    render() {
        const { feedbacks, title, subtitle, scope } = this.props;
        const { viewMode, selectedWord, animateKey } = this.state;

        const { words, cloudWords, totalResponses, totalKeywords, maxCount } = this.processWords(feedbacks);

        if (totalResponses === 0 || words.length === 0) {
            return (
                <div className="wordcloud-container">
                    <div className="wordcloud-header">
                        <div className="wordcloud-titles">
                            <span className="wordcloud-badge">
                                {scope === 'all' ? 'All-Time Feedback' : 'Open-Ended Feedback'}
                            </span>
                            <h3 className="wordcloud-main-title">{title || 'Feedback Word Cloud'}</h3>
                            <p className="wordcloud-subtitle">
                                {subtitle || 'Key themes from open-ended responses'}
                            </p>
                        </div>
                    </div>
                    <div className="wordcloud-empty">
                        <span className="empty-icon">💭</span>
                        <p>
                            No open-ended text answers collected yet.<br />
                            Once your respondents submit qualitative feedback, an animated word cloud and frequency tree will bloom here automatically!
                        </p>
                    </div>
                </div>
            );
        }

        const topWord = words[0];

        return (
            <div className="wordcloud-container">
                <div className="wordcloud-header">
                    <div className="wordcloud-titles">
                        <span className="wordcloud-badge">
                            {scope === 'all' ? '⚡ All-Time Feedback Intelligence' : '💬 Page Feedback Themes'}
                        </span>
                        <h3 className="wordcloud-main-title">{title || 'Feedback Word Cloud'}</h3>
                        <p className="wordcloud-subtitle">
                            {subtitle || `Synthesized from ${totalResponses} open-ended response${totalResponses === 1 ? '' : 's'}`}
                        </p>
                    </div>

                    <div className="wordcloud-actions">
                        <button
                            className={`view-toggle-btn ${viewMode === 'cloud' ? 'active' : ''}`}
                            onClick={() => this.setState({ viewMode: 'cloud' })}
                            title="Switch to Animated Cloud View"
                        >
                            ☁️ Cloud
                        </button>
                        <button
                            className={`view-toggle-btn ${viewMode === 'tree' ? 'active' : ''}`}
                            onClick={() => this.setState({ viewMode: 'tree' })}
                            title="Switch to Frequency Tree View"
                        >
                            📊 Tree
                        </button>
                        <button
                            className="reanimate-btn"
                            onClick={this.handleReanimate}
                            title="Re-animate Cloud"
                        >
                            ↻ Animate
                        </button>
                    </div>
                </div>

                <div className="wordcloud-metrics-bar">
                    <div className="metric-chip">
                        <span className="metric-icon">📝</span>
                        <span><strong>{totalResponses}</strong> Open Response{totalResponses === 1 ? '' : 's'}</span>
                    </div>
                    <div className="metric-chip">
                        <span className="metric-icon">🔤</span>
                        <span><strong>{totalKeywords}</strong> Unique Themes</span>
                    </div>
                    {topWord && (
                        <div className="metric-chip">
                            <span className="metric-icon">⭐</span>
                            <span>
                                Top Word: <strong>"{topWord.word}"</strong> ({topWord.count} mention{topWord.count === 1 ? '' : 's'})
                            </span>
                        </div>
                    )}
                </div>

                {viewMode === 'cloud' ? (
                    <div className="wordcloud-canvas" key={animateKey}>
                        {cloudWords.map((item, index) => {
                            const isSelected = selectedWord && selectedWord.word === item.word;
                            const delay = Math.min(index * 35, 1000);

                            return (
                                <div
                                    key={item.word}
                                    className={`cloud-word ${item.tier} ${isSelected ? 'selected' : ''}`}
                                    style={{
                                        fontSize: `${item.fontSize}px`,
                                        animationDelay: `${delay}ms`,
                                        outline: isSelected ? '2px solid #0b0e11' : 'none',
                                    }}
                                    onClick={() => this.handleWordClick(item)}
                                    title={`"${item.word}": mentioned ${item.count} time${item.count === 1 ? '' : 's'} (${item.percentage}% of responses). Click to view quotes.`}
                                >
                                    <span className="word-text">{item.word}</span>
                                    <span className="word-count-badge">{item.count}</span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="wordcloud-tree-view">
                        {words.map((item, index) => {
                            const barWidth = Math.max(Math.round((item.count / maxCount) * 100), 4);
                            const isSelected = selectedWord && selectedWord.word === item.word;

                            return (
                                <div
                                    key={item.word}
                                    className={`tree-bar-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => this.handleWordClick(item)}
                                    title={`Click to view responses mentioning "${item.word}"`}
                                >
                                    <span className="tree-rank">#{index + 1}</span>
                                    <span className="tree-word">{item.word}</span>
                                    <div className="tree-bar-wrapper">
                                        <div
                                            className="tree-bar-fill"
                                            style={{ width: `${barWidth}%` }}
                                        />
                                    </div>
                                    <span className="tree-stats">
                                        {item.count} {item.count === 1 ? 'mention' : 'mentions'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Interactive drawer displaying all quotes containing the clicked word */}
                {selectedWord && (
                    <div className="quotes-drawer">
                        <div className="quotes-drawer-header">
                            <div className="drawer-title">
                                Responses mentioning <span className="highlighted-word">"{selectedWord.word}"</span> ({selectedWord.quotes.length}):
                            </div>
                            <button
                                className="drawer-close-btn"
                                onClick={() => this.setState({ selectedWord: null })}
                            >
                                ✕ Close
                            </button>
                        </div>
                        <div className="drawer-quotes-list">
                            {selectedWord.quotes.map((quote, qIndex) => (
                                <div className="drawer-quote-item" key={qIndex}>
                                    "{this.highlightText(quote, selectedWord.word)}"
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

WordCloud.propTypes = {
    feedbacks: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    scope: PropTypes.oneOf(['all', 'page']),
};

WordCloud.defaultProps = {
    feedbacks: [],
    title: 'Feedback Themes & Word Cloud',
    subtitle: 'Derived from open-ended responses',
    scope: 'page',
};

export default WordCloud;
